import { useCallback, useEffect, useRef } from 'react';
import { GTM } from '@src/analytics/gtm';
import { useWebEngage } from '@src/hooks/useWebEngage';
import {
  gtmEvents,
  BROCHURE_SCROLL_THRESHOLDS,
} from '@src/analytics/gtmEventsConstant';

// Centralises every analytics dispatch for the brochure view page.
//
// - Fires BROCHURE_PAGE_VIEWED once on mount (GTM + WebEngage).
// - Exposes a scroll-depth tracker that fires each 25/50/75/100% threshold
//   exactly once per page load.
// - Exposes small helpers for the bookmark, scrubber, CTA and zoom events so
//   consumers don't have to import the constants themselves.
const useBrochureAnalytics = ({ programSlug, programName }) => {
  const { captureUserClickEvent } = useWebEngage();
  const firedThresholds = useRef(new Set());
  const didFirePageView = useRef(false);

  const track = useCallback(
    (event, payload = {}) => {
      const enriched = {
        programSlug,
        programName,
        ...payload,
      };
      try {
        GTM.track(event, enriched);
      } catch (err) {
        // swallow — analytics must never break the page
      }
      try {
        captureUserClickEvent(event, enriched);
      } catch (err) {
        // swallow
      }
    },
    [programSlug, programName, captureUserClickEvent],
  );

  useEffect(() => {
    if (didFirePageView.current) return;
    didFirePageView.current = true;
    track(gtmEvents.BROCHURE_PAGE_VIEWED);
  }, [track]);

  const reportScrollDepth = useCallback(
    (pct) => {
      for (const { pct: threshold, event } of BROCHURE_SCROLL_THRESHOLDS) {
        if (pct >= threshold && !firedThresholds.current.has(threshold)) {
          firedThresholds.current.add(threshold);
          track(event, { scrollPct: Math.round(pct) });
        }
      }
    },
    [track],
  );

  const reportBookmarkClick = useCallback(
    (bookmark) =>
      track(gtmEvents.BROCHURE_BOOKMARK_CLICKED, {
        label: bookmark?.label,
        page: bookmark?.page,
      }),
    [track],
  );

  const reportScrubberDrag = useCallback(
    (fromPage, toPage) =>
      track(gtmEvents.BROCHURE_SCRUBBER_DRAGGED, { fromPage, toPage }),
    [track],
  );

  const reportDemoCtaClick = useCallback(
    () => track(gtmEvents.BROCHURE_DEMO_CTA_CLICKED),
    [track],
  );

  const reportAdvisorCtaClick = useCallback(
    () => track(gtmEvents.BROCHURE_ADVISOR_CTA_CLICKED),
    [track],
  );

  const reportZoom = useCallback(
    (direction) =>
      track(
        direction === 'in'
          ? gtmEvents.BROCHURE_ZOOM_IN
          : gtmEvents.BROCHURE_ZOOM_OUT,
      ),
    [track],
  );

  return {
    reportScrollDepth,
    reportBookmarkClick,
    reportScrubberDrag,
    reportDemoCtaClick,
    reportAdvisorCtaClick,
    reportZoom,
  };
};

export default useBrochureAnalytics;
